import type { TFunction } from 'i18next';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { type MappedAssetSensor, SensorTypeCode } from '@/common/asset-sensor';
import { ROLE } from '@/common/role';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

import { Restricted } from '../Restricted';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../ui/resizable';
import { AccessDeniedView } from './AccessDeniedView';
import { MeteoTimeSeries } from './charts/met/TimeSeries';
import { Statistics } from './charts/statistics';
import { TpsTimeSeries } from './charts/tps/TimeSeries';
import { TpsTimeSeriesVector } from './charts/tps/TimeSeriesVector';
import { PerformanceDash } from './dashboards/PerformanceDash';
import { SectionCharts } from './dashboards/SectionCharts';
import { EmptyChart } from './EmptyChart';
import { SensorChartTabs } from './sensorChartTabs';
import { DASHBOARD_ID, Dashboards } from './tabs/dashbords';
import { SensorMapContainer } from './tabs/map/SensorMapContainer';
import { Sensors } from './tabs/sensors';

const RIGHT_PANEL_VIEWS = {
	SENSOR_CHART: 'SENSOR_CHART',
	...DASHBOARD_ID,
} as const;
type RIGHT_PANEL_VIEWS = (typeof RIGHT_PANEL_VIEWS)[keyof typeof RIGHT_PANEL_VIEWS];

const MOBILE_CHART_TYPE = {
	TIMESERIES: 'TIMESERIES',
	TIMESERIES_VECTOR: 'TIMESERIES_VECTOR',
	STATISTICS: 'STATISTICS',
} as const;

function getMobileChartOptions(sensor: MappedAssetSensor | null, t: TFunction) {
	if (!sensor) return [];
	if (sensor.type === SensorTypeCode.TPS) {
		return [
			{ value: MOBILE_CHART_TYPE.TIMESERIES, label: t('health.charts.timeseries') },
			{ value: MOBILE_CHART_TYPE.TIMESERIES_VECTOR, label: t('health.charts.timeseriesVector') },
			{ value: MOBILE_CHART_TYPE.STATISTICS, label: t('health.charts.statistics') },
		];
	}
	if (sensor.type === SensorTypeCode.MET) {
		return [
			{ value: MOBILE_CHART_TYPE.TIMESERIES, label: t('health.charts.timeseries') },
			{ value: MOBILE_CHART_TYPE.STATISTICS, label: t('health.charts.statistics') },
		];
	}
	return [];
}

const MOBILE_TAB_LABELS = (t: TFunction): Record<string, string> => ({
	map: t('health.tabs.map'),
	dashboards: t('health.tabs.dashboards'),
	sensors: t('health.tabs.sensors'),
	graphics: t('health.tabs.graphics'),
});

const DESKTOP_TAB_CLASS =
	'rounded-none border-0 border-b-2 border-transparent data-[state=active]:border-primary! data-[state=active]:bg-transparent data-[state=active]:text-primary! bg-transparent! text-muted-foreground hover:text-primary! hover:cursor-pointer px-4 py-2';

export function HealthMonitoringLayout() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { assetId } = useParams<{ assetId: string }>();

	const [searchParams] = useSearchParams();
	const sensorIdFromUrl = searchParams.get('sensorId');

	const [activeTab, setActiveTab] = useState<string>('map');
	const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
	const [mobileSensorChartType, setMobileSensorChartType] = useState(MOBILE_CHART_TYPE.TIMESERIES);

	const [selectedSensor, setSelectedSensor] = useState<MappedAssetSensor | null>(null);
	const [rightPanelView, setRightPanelView] = useState<RIGHT_PANEL_VIEWS | ''>('');
	const [selectedDashboardId, setSelectedDashboardId] = useState<DASHBOARD_ID | null>(null);

	useEffect(() => {
		if (sensorIdFromUrl) {
			setActiveTab('sensors');
			setRightPanelView(RIGHT_PANEL_VIEWS.SENSOR_CHART);
		}
	}, [sensorIdFromUrl]);

	useEffect(() => {
		const check = () => setIsMobile(window.innerWidth < 1024);
		window.addEventListener('resize', check);
		return () => window.removeEventListener('resize', check);
	}, []);

	useEffect(() => {
		setMobileSensorChartType(MOBILE_CHART_TYPE.TIMESERIES);
	}, [selectedSensor?.uniqId]);

	const onSelectSensor = (sensor: MappedAssetSensor) => {
		setRightPanelView(RIGHT_PANEL_VIEWS.SENSOR_CHART);
		setSelectedSensor(sensor);
		setSelectedDashboardId(null);
		navigate(`?selectedSensorId=${sensor.uniqId}`, { replace: true });
		if (isMobile) setActiveTab('graphics');
	};

	const onSelectDashboard = (dashboardId: DASHBOARD_ID) => {
		setRightPanelView(dashboardId);
		setSelectedDashboardId(dashboardId);
		setSelectedSensor(null);
		if (isMobile) setActiveTab('graphics');
	};

	if (!assetId) {
		return <div className="p-6">{t('health.layout.assetIdMissing')}</div>;
	}

	const chartOptions = getMobileChartOptions(selectedSensor, t);
	const mobileTabLabels = MOBILE_TAB_LABELS(t);

	const mobileChartContent = (() => {
		if (!selectedSensor) return null;
		switch (mobileSensorChartType) {
			case MOBILE_CHART_TYPE.TIMESERIES:
				if (selectedSensor.type === SensorTypeCode.TPS)
					return <TpsTimeSeries assetId={assetId} selectedSensor={selectedSensor} />;
				if (selectedSensor.type === SensorTypeCode.MET)
					return <MeteoTimeSeries assetId={assetId} selectedSensor={selectedSensor} />;
				return null;
			case MOBILE_CHART_TYPE.TIMESERIES_VECTOR:
				return <TpsTimeSeriesVector assetId={assetId} selectedSensor={selectedSensor} />;
			case MOBILE_CHART_TYPE.STATISTICS:
				return <Statistics assetId={assetId} selectedSensor={selectedSensor} />;
			default:
				return null;
		}
	})();

	return (
		<div className="h-[calc(100vh-64px)] w-full flex flex-col bg-background">
			{/* Header */}
			<div className="bg-card border-b flex-none text-card-foreground">
				<div className="p-4 md:p-6 md:pb-4 flex flex-col gap-1 lg:grid lg:grid-cols-[auto_1fr] lg:items-center lg:gap-4">
					<Button
						variant="ghost"
						className="text-muted-foreground hover:text-primary hover:bg-transparent! px-0! gap-0 hover:pl-2! cursor-pointer w-fit"
						onClick={() => navigate(-1)}
					>
						<ArrowLeft className="size-4 mr-1!" />
						{t('common.actions.back')}
					</Button>
					<h1 className="text-xl md:text-2xl font-bold">{t('health.layout.title')}</h1>
				</div>
			</div>

			{isMobile ? (
				/* ── MOBILE / TABLET ── */
				<div className="flex-1 flex flex-col overflow-hidden">
					{/* Tab bar: Map | Dashboards | Sensors | Graphics */}
					<div className="flex border-b bg-card flex-none overflow-x-auto">
						{(['map', 'dashboards', 'sensors', 'graphics'] as const).map((tab) => (
							<button
								key={tab}
								type="button"
								onClick={() => setActiveTab(tab)}
								className={cn(
									'flex-1 py-3 text-sm font-medium transition-colors whitespace-nowrap px-3',
									activeTab === tab
										? 'border-b-2 border-primary text-primary'
										: 'text-muted-foreground hover:text-foreground',
								)}
							>
								{mobileTabLabels[tab]}
							</button>
						))}
					</div>

					{/* Map */}
					{activeTab === 'map' && (
						<div className="flex-1 overflow-hidden">
							<Restricted to={ROLE.SENSORS_READ} fallback={<AccessDeniedView />}>
								<SensorMapContainer assetId={assetId} selectedSensor={selectedSensor} onSelectSensor={onSelectSensor} />
							</Restricted>
						</div>
					)}

					{/* Dashboards */}
					{activeTab === 'dashboards' && (
						<div className="flex-1 overflow-auto p-4">
							<Dashboards onSelectDashboard={onSelectDashboard} selectedDashboardId={selectedDashboardId} />
						</div>
					)}

					{/* Sensors */}
					{activeTab === 'sensors' && (
						<div className="flex-1 overflow-auto p-4">
							<Restricted to={ROLE.SENSORS_READ} fallback={<AccessDeniedView />}>
								<Sensors assetId={assetId} selectedSensor={selectedSensor} onSelectSensor={onSelectSensor} />
							</Restricted>
						</div>
					)}

					{/* Graphics */}
					{activeTab === 'graphics' && (
						<div className="flex-1 flex flex-col overflow-hidden">
							{selectedSensor && chartOptions.length > 0 ? (
								<>
									<div className="px-4 py-3 border-b bg-card flex-none">
										<Select value={mobileSensorChartType} onValueChange={setMobileSensorChartType}>
											<SelectTrigger className="w-full">
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{chartOptions.map((opt) => (
													<SelectItem key={opt.value} value={opt.value}>
														{opt.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
									<div className="flex-1 overflow-auto p-4 bg-background">{mobileChartContent}</div>
								</>
							) : selectedDashboardId ? (
								<div className="flex-1 overflow-auto p-4 bg-background">
									{selectedDashboardId === DASHBOARD_ID.TPS_SENSOR_COMPUTATIONS_DASHBOARD && (
										<PerformanceDash assetId={assetId} />
									)}
									{selectedDashboardId === DASHBOARD_ID.TPS_SECTION_CHART_DASHBOARD && (
										<SectionCharts assetId={assetId} />
									)}
								</div>
							) : (
								<div className="flex-1 flex items-center justify-center p-6">
									<p className="text-sm text-muted-foreground text-center">
										{t('health.empty.selectSensorForGraphics')}
									</p>
								</div>
							)}
						</div>
					)}
				</div>
			) : (
				/* ── DESKTOP ── */
				<ResizablePanelGroup direction="horizontal" className="flex-1">
					<ResizablePanel defaultSize={30} minSize={20} className="bg-card">
						<div className="h-full flex flex-col">
							<Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden gap-0">
								<TabsList className="justify-start rounded-none border-b border-transparent bg-transparent p-0">
									<TabsTrigger value="map" className={DESKTOP_TAB_CLASS}>
										{t('health.tabs.map')}
									</TabsTrigger>
									<TabsTrigger value="dashboards" className={DESKTOP_TAB_CLASS}>
										{t('health.tabs.dashboards')}
									</TabsTrigger>
									<TabsTrigger value="sensors" className={DESKTOP_TAB_CLASS}>
										{t('health.tabs.sensors')}
									</TabsTrigger>
								</TabsList>
								<TabsContent value="map" className="flex-1 overflow-auto m-0 bg-background">
									<Restricted to={ROLE.SENSORS_READ} fallback={<AccessDeniedView />}>
										<SensorMapContainer
											assetId={assetId}
											selectedSensor={selectedSensor}
											onSelectSensor={onSelectSensor}
										/>
									</Restricted>
								</TabsContent>
								<TabsContent value="sensors" className="flex-1 overflow-auto p-6 m-0 bg-background">
									<Restricted to={ROLE.SENSORS_READ} fallback={<AccessDeniedView />}>
										<Sensors assetId={assetId} selectedSensor={selectedSensor} onSelectSensor={onSelectSensor} />
									</Restricted>
								</TabsContent>
								<TabsContent value="dashboards" className="flex-1 overflow-auto p-6 m-0 bg-background">
									<Dashboards onSelectDashboard={onSelectDashboard} selectedDashboardId={selectedDashboardId} />
								</TabsContent>
							</Tabs>
						</div>
					</ResizablePanel>
					<ResizableHandle className="w-1 bg-muted-foreground hover:bg-blue-500 transition-colors" />
					<ResizablePanel className="bg-background relative" minSize={40}>
						<div className="h-full flex flex-col">
							{rightPanelView === '' && <EmptyChart />}
							{rightPanelView === RIGHT_PANEL_VIEWS.SENSOR_CHART && (
								<SensorChartTabs assetId={assetId} selectedSensor={selectedSensor} />
							)}
							{rightPanelView === RIGHT_PANEL_VIEWS.TPS_SENSOR_COMPUTATIONS_DASHBOARD && (
								<PerformanceDash assetId={assetId} />
							)}
							{rightPanelView === RIGHT_PANEL_VIEWS.TPS_SECTION_CHART_DASHBOARD && <SectionCharts assetId={assetId} />}
						</div>
					</ResizablePanel>
				</ResizablePanelGroup>
			)}
		</div>
	);
}
